import {
  ApiError,
  ERROR_KINDS,
  REQUEST_TIMEOUT_MS,
  extractServerMessages,
  httpErrorText,
  parseErrorText,
  toTransportError,
  toUnexpectedError,
} from "./apiErrors";

import { notifyWith } from "./notifier";

import { getAuth } from "./authStore";

import type { AuthData, HttpMethod, Notifier, NotifyType, RequestConfig, RequestOptions } from "types";

const METHODS: Record<HttpMethod, HttpMethod> = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  DELETE: "DELETE",
};

const DEV_PROXY = process.env.NODE_ENV === "development";
const TARGET_HEADER = "X-Proxy-Target";

// The app wide fallback toast setter lives in ./notifier; re-exported here because the
// api modules and NotifyProvider have always reached for it through this module.
export { registerNotifier } from "./notifier";

export default class FetchRequest {
  // Guards the legacy call sites that still pass a base url string in here.
  private readonly setNotifObj: Notifier | null;

  constructor(setNotifObj?: unknown) {
    this.setNotifObj = typeof setNotifObj === "function" ? (setNotifObj as Notifier) : null;
  }

  // Pages used to hand their `context.authData` to every single call. The pair now
  // lives in authStore, so an omitted argument is the normal case; an explicit one
  // still wins for the rare call that needs to talk to another domain.
  resolveAuth = (authdata?: AuthData | null): AuthData =>
    authdata && authdata.token ? authdata : getAuth();

  // Absolute in a build, relative behind the dev proxy.
  resolveUrl = (url: string, authdata: AuthData): string =>
    DEV_PROXY ? url : `${authdata?.domain || ""}${url}`;

  get = <T = unknown>(url: string, authdata?: AuthData | null, options?: RequestOptions): Promise<T> => {
    const auth = this.resolveAuth(authdata);

    return this.request<T>(this.resolveUrl(url, auth), {
      ...options,
      method: METHODS.GET,
      token: auth?.token,
      domain: auth?.domain,
    });
  };

  post = <T = unknown>(url: string, options?: RequestOptions, authdata?: AuthData | null): Promise<T> => {
    const auth = this.resolveAuth(authdata);

    return this.request<T>(this.resolveUrl(url, auth), {
      ...options,
      method: METHODS.POST,
      token: auth?.token,
      domain: auth?.domain,
    });
  };

  put = <T = unknown>(url: string, options?: RequestOptions, authdata?: AuthData | null): Promise<T> => {
    const auth = this.resolveAuth(authdata);

    return this.request<T>(this.resolveUrl(url, auth), {
      ...options,
      method: METHODS.PUT,
      token: auth?.token,
      domain: auth?.domain,
    });
  };

  delete = <T = unknown>(url: string, options?: RequestOptions, authdata?: AuthData | null): Promise<T> => {
    const auth = this.resolveAuth(authdata);

    return this.request<T>(this.resolveUrl(url, auth), {
      ...options,
      method: METHODS.DELETE,
      token: auth?.token,
      domain: auth?.domain,
    });
  };

  notify = (type: NotifyType, text?: string): void => {
    notifyWith(this.setNotifObj, type, text);
  };

  // 201 and 202 are the only confirmation a write ever gets.
  notifyWrite = (status: number, data?: unknown): void => {
    if (status === 201 && !(data && (data as { columns?: unknown }).columns)) {
      this.notify("success", "Created");
    }

    if (status === 202) {
      this.notify("success", "Updated");
    }
  };

  // A failed answer carries JSON, an HTML debug page, or nothing at all. Whatever it
  // is, reading it must not produce another error on top of the one being reported.
  readErrorBody = (response: Response, isJson: boolean): Promise<unknown> => {
    if (isJson) {
      return response.json().catch(() => null);
    }

    return response.text().then(
      (text) => text || null,
      () => null
    );
  };

  handleResponse = <T = unknown>(response: Response, method: HttpMethod, url: string): Promise<T> => {
    const isJson = Boolean(response.headers.get("content-type")?.includes("application/json"));

    // Every 4xx and 5xx ends up here. Before, only 500 and 422 were looked at and
    // the rest fell through into the success branch: the error body was handed to
    // the page as if it were data, with no toast at all.
    if (!response.ok) {
      return this.readErrorBody(response, isJson).then((body) => {
        // A 422 carrying no error list is not a validation failure. Some endpoints
        // report business state that way and the original code returned it to the
        // caller, so that stays as it was.
        if (response.status === 422 && isJson && body && !extractServerMessages(body).length) {
          return body as T;
        }

        throw new ApiError(httpErrorText(response.status, method, url, body), {
          kind: response.status === 422 ? ERROR_KINDS.validation : ERROR_KINDS.http,
          status: response.status,
          method: method,
          url: url,
          body: body,
        });
      });
    }

    if (!isJson) {
      this.notifyWrite(response.status);

      return Promise.resolve(true as unknown as T);
    }

    return response.json().then(
      (data) => {
        const messages = data ? extractServerMessages({ messages: data.messages }) : [];

        if (messages.length > 0) {
          this.notify("success", messages.join("\n"));

          return data as T;
        }

        this.notifyWrite(response.status, data);

        return data as T;
      },
      () => {
        throw new ApiError(parseErrorText(response.status, method, url), {
          kind: ERROR_KINDS.parse,
          status: response.status,
          method: method,
          url: url,
        });
      }
    );
  };

  request = <T = unknown>(url: string, options: RequestConfig, timeout = REQUEST_TIMEOUT_MS): Promise<T> => {
    const { headers = {}, method, data, token, domain } = options;
    const payload = data ? (headers.contentType ? (data as BodyInit) : JSON.stringify({ ...data })) : null;
    const config: RequestInit & { headers: Record<string, string> } = {
      headers: headers.contentType
        ? {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          }
        : {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
      method: method,
      mode: "cors",
      credentials: "omit",
    };

    if (DEV_PROXY && domain) {
      config.headers[TARGET_HEADER] = domain;
    }

    if (method !== "GET") {
      config.body = payload;
    }

    // Values come straight from filter inputs, so an unescaped &, = or # used to break
    // the query or smuggle an extra parameter into it. `typeof null` is "object", hence
    // the explicit data check.
    let target = url;

    if (data && typeof data === "object" && method === "GET") {
      const bag = data as Record<string, unknown>;
      const qs = Object.keys(bag)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(bag[key]))}`)
        .join("&");

      target = `${url}?${qs}`;
    }

    // Abort a hung request instead of leaving the page loading forever. The original
    // code declared this timeout but never wired it into fetch().
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (controller) {
      config.signal = controller.signal;

      if (timeout > 0) {
        timer = setTimeout(() => controller.abort(), timeout);
      }
    }

    const stopTimer = (): void => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    // Single funnel for every failure: the real message in the toast, the details in
    // the console, and the rejection keeps travelling up instead of being swallowed.
    const fail = (error: ApiError): never => {
      this.notify("error", error.message);

      console.error(`[api] ${error.message}`, {
        method: method,
        url: target,
        status: error.status,
        kind: error.kind,
        body: error.body,
      });

      throw error;
    };

    return fetch(target, config)
      .then(
        (response) => {
          stopTimer();

          return this.handleResponse<T>(response, method, target);
        },
        (error) => {
          stopTimer();

          throw toTransportError(error, method, target, timeout);
        }
      )
      .catch((error) => fail(toUnexpectedError(error, method, target)));
  };
}
